import { Fragment, useEffect, useState, useRef } from "react";
import {
  IonApp,
  IonRouterOutlet,
  IonSpinner,
  setupIonicReact,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Provider } from "react-redux";
import { Redirect, Route, useLocation } from "react-router-dom";
import { SplashScreen } from "@capacitor/splash-screen";

import "./App.css";
import Home from "./pages/Home/index";
import SharePage from "./pages/Share/SharePage";
import GuestConverter from "./pages/GuestConverter";
import AutoDigitizePlayground from "./pages/AutoDigitizePlayground";
import { handleOAuthRedirectResult, createOAuthUserDocument } from "./lib/firebase/firebase";
import { getStoredPendingFile, claimPendingFile, clearPendingFile } from "./lib/firebase/pendingFiles";
import { getUTMForFirebase } from "./lib/analytics/utmTracking";
import { initializeGa4, trackGaPageView } from "./lib/analytics/ga4";
import { trackPageView } from "./lib/analytics/metaPixel";
import { isNativeiOS } from "./lib/utils/platform";
import { useAuth } from "./contexts/AuthContext";
import { appName, isEmbroideryTrace } from "./config/appVariant";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";
/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";

/* Theme variables */
import { AuthProvider } from "./contexts/AuthContext";
import { ProcessedImageProvider } from "./contexts/ProcessedImageContext";
import { OnboardingProvider } from "./components/Onboarding";
import { TutorialProvider, TutorialOverlay, TutorialRecorder } from "./components/Tutorial";
import IntercomProvider from "./components/IntercomProvider";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import PrivateRoute from "./pages/Auth/PrivateRoute";
import SignIn from "./pages/Auth/SignIn";
import SignUp from "./pages/Auth/SignUp";
import "./theme/variables.css";
import Checkout from "./pages/Checkout";
import WelcomeBack from "./pages/WelcomeBack/WelcomeBack";
import { store } from "./redux/store";
import svgTraceLogo from "./assets/logo.svg";

// EmbroideryTrace intentionally runs without Intercom.
const ChatProvider = isEmbroideryTrace ? Fragment : IntercomProvider;

setupIonicReact();

const MetaPixelPageTracker = () => {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the initial render — the base snippet in index.html already fires PageView on load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackPageView();
  }, [location.pathname]);

  return null;
};

const GaPageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackGaPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const NativeLoadingGate = ({ children }: { children: React.ReactNode }) => {
  const { isLoading } = useAuth();
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [overlayGone, setOverlayGone] = useState(false);
  const splashHidden = useRef(false);

  useEffect(() => {
    if (!isNativeiOS()) {
      document.getElementById("native-loading")?.remove();
      return;
    }
    const timer = setTimeout(() => setMinTimeElapsed(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isNativeiOS()) {
      document.getElementById("native-loading")?.remove();
      if (!splashHidden.current) {
        splashHidden.current = true;
        SplashScreen.hide({ fadeOutDuration: 200 });
      }
    }
  }, []);

  const ready = !isLoading && minTimeElapsed;

  useEffect(() => {
    if (!isNativeiOS() || !ready) return;
    const timer = setTimeout(() => setOverlayGone(true), 350);
    return () => clearTimeout(timer);
  }, [ready]);

  if (!isNativeiOS()) return <>{children}</>;

  return (
    <>
      {children}
      {!overlayGone && (
        <div className={`native-loading-overlay${ready ? " native-loading-overlay--ready" : ""}`}>
          <img className="native-loading-logo" src={svgTraceLogo} alt={appName} />
          <IonSpinner
            className="native-loading-spinner"
            name="crescent"
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
          <p className="native-loading-label">Loading</p>
        </div>
      )}
    </>
  );
};

const App = () => {
  const [oauthHandled, setOauthHandled] = useState(false);
  const [, setOrientation] = useState(() =>
    window.innerWidth > window.innerHeight ? "landscape" : "portrait"
  );

  useEffect(() => {
    initializeGa4();
    document.title = appName;
    if (isEmbroideryTrace) {
      document.body.classList.add("emb-variant");
    }
  }, []);

  // Force re-render + viewport recalculation on orientation change (iPad)
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const updateViewport = () => {
      const next = window.innerWidth > window.innerHeight ? "landscape" : "portrait";
      setOrientation(next);
      document.documentElement.style.setProperty("--app-vh", `${window.innerHeight * 0.01}px`);
    };

    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updateViewport, 100);
    };

    const handleOrientationChange = () => {
      // iOS fires orientationchange before dimensions update; wait for layout
      setTimeout(() => {
        updateViewport();
        window.dispatchEvent(new Event("resize"));
      }, 150);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    // Set initial value
    document.documentElement.style.setProperty("--app-vh", `${window.innerHeight * 0.01}px`);

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  // Handle OAuth redirect result on app initialization
  // Only needed on web — native iOS uses Capacitor-native sign-in, not web redirects.
  // Calling getRedirectResult inside WKWebView blocks onAuthStateChanged indefinitely.
  useEffect(() => {
    if (isNativeiOS()) {
      setOauthHandled(true);
      return;
    }

    const handleRedirect = async () => {
      try {
        const result = await handleOAuthRedirectResult();
        
        if (result) {
          console.log("✅ OAuth redirect completed:", result.user?.email);
          
          // Set a flag so SignIn knows to redirect immediately without waiting for premium status
          sessionStorage.setItem('oauthRedirectComplete', 'true');
          
          // Check if this is a new user and create their document
          // @ts-ignore - additionalUserInfo exists on the result
          if (result.additionalUserInfo?.isNewUser) {
            const utmData = getUTMForFirebase();
            await createOAuthUserDocument(result.user.uid, result.user.email || "", utmData);
            console.log("✅ Created OAuth user document for new user");
          }

          // Check for pending file to claim
          const pendingData = getStoredPendingFile() as { fileId?: string } | null;
          if (pendingData?.fileId) {
            console.log("🎯 Found pending file after OAuth redirect:", pendingData.fileId);
            try {
              await claimPendingFile(pendingData.fileId);
              console.log("✅ Pending file claimed after OAuth redirect");
              // Don't clear yet - let Home page load the file
            } catch (claimError) {
              console.error("Failed to claim pending file:", claimError);
              clearPendingFile();
            }
          }
        }
      } catch (error) {
        console.error("OAuth redirect handling error:", error);
      } finally {
        setOauthHandled(true);
      }
    };

    handleRedirect();
  }, []);

  return (
    <Provider store={store}>
      <IonApp>
        <AuthProvider>
          <ChatProvider>
          <NativeLoadingGate>
          <ProcessedImageProvider>
            <TutorialProvider>
              <OnboardingProvider>
              <IonReactRouter>
              <MetaPixelPageTracker />
              <GaPageTracker />
              <IonRouterOutlet
                animated={false}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <Redirect exact path="/" to="/home" />
                <Route exact path="/signup" component={SignUp} />
                <Route exact path="/signin" component={SignIn} />
                <Route
                  exact
                  path="/forgot-password"
                  component={ForgotPassword}
                />
                <Route exact path="/checkout" component={Checkout} />
                <Route exact path="/welcome-back" component={WelcomeBack} />
                {/* Public community share page - no authentication required */}
                <Route exact path="/community/:slug" component={SharePage} />
                {/* Guest converter - public access for users from marketing site */}
                <Route exact path="/convert" component={GuestConverter} />
                <PrivateRoute exact path="/home" component={Home} />
                {process.env.NODE_ENV === "development" ? (
                  <PrivateRoute
                    exact
                    path="/playground/auto-digitize"
                    component={AutoDigitizePlayground}
                  />
                ) : (
                  <Redirect
                    exact
                    path="/playground/auto-digitize"
                    to="/home"
                  />
                )}
              </IonRouterOutlet>
              </IonReactRouter>
              <TutorialOverlay />
              <TutorialRecorder />
              </OnboardingProvider>
            </TutorialProvider>
          </ProcessedImageProvider>
          </NativeLoadingGate>
          </ChatProvider>
        </AuthProvider>
      </IonApp>
    </Provider>
  );
};

export default App;
